import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, Search, MessageCircle, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const SOCIAL_SOURCES = ['hacker news', 'dev community', 'reddit', 'stack overflow blog', 'product hunt']

const POSITIVE_WORDS = ['great', 'awesome', 'love', 'excellent', 'amazing', 'breakthrough', 'impressive', 'innovative', 'success', 'powerful', 'revolutionar']
const NEGATIVE_WORDS = ['terrible', 'broken', 'fail', 'worst', 'awful', 'critical', 'vulnerability', 'breach', 'outage', 'disaster', 'exploit', 'attack', 'hack']

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

function analyzeSentiment(text: string): 'positive' | 'negative' | 'neutral' {
  const lower = text.toLowerCase()
  const posScore = POSITIVE_WORDS.filter(w => lower.includes(w)).length
  const negScore = NEGATIVE_WORDS.filter(w => lower.includes(w)).length
  if (posScore > negScore) return 'positive'
  if (negScore > posScore) return 'negative'
  return 'neutral'
}

const SENTIMENT_CONFIG: Record<string, { badge: string; dot: string; label: string }> = {
  positive: { badge: 'badge-green', dot: 'bg-emerald', label: 'Positive' },
  negative: { badge: 'badge-red', dot: 'bg-rose', label: 'Negative' },
  neutral:  { badge: 'badge-cyan', dot: 'bg-text-muted', label: 'Neutral' },
}

export default function SentimentPage() {
  const [articles, setArticles] = useState<any[]>([])
  const [sentimentFilter, setSentimentFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/news/feed', { params: { limit: 100 } })
      const all = data.articles || data.items || data || []
      // Filter for social/community sources
      const social = all.filter((a: any) => {
        const src = (a.source || '').toLowerCase()
        return SOCIAL_SOURCES.some(s => src.includes(s))
      })
      setArticles(social)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  const enriched = articles.map(a => ({
    ...a,
    sentiment: analyzeSentiment(`${a.title || ''} ${a.summary || ''} ${a.description || ''}`),
  }))

  const counts = {
    positive: enriched.filter(a => a.sentiment === 'positive').length,
    negative: enriched.filter(a => a.sentiment === 'negative').length,
    neutral: enriched.filter(a => a.sentiment === 'neutral').length,
  }

  const filtered = enriched.filter(item => {
    if (sentimentFilter !== 'all' && item.sentiment !== sentimentFilter) return false
    if (searchTerm) {
      const text = `${item.title || ''} ${item.summary || ''} ${item.source || ''}`.toLowerCase()
      if (!text.includes(searchTerm.toLowerCase())) return false
    }
    return true
  })

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <MessageCircle size={24} className="text-purple" /> Social &amp; Community Sentiment
          </h1>
          <p className="text-sm text-text-secondary mt-1">Hacker News, Reddit, DEV Community, Stack Overflow</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="stat-card">
          <p className="stat-label">Positive</p>
          <p className="stat-value text-emerald">{counts.positive}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Negative</p>
          <p className="stat-value text-rose">{counts.negative}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Neutral</p>
          <p className="stat-value text-text-muted">{counts.neutral}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search articles..."
            className="input pl-9 py-2 text-xs"
          />
        </div>
        <select
          title=""
          value={sentimentFilter}
          onChange={e => setSentimentFilter(e.target.value)}
          className="input w-auto py-2 text-xs"
        >
          <option value="all">All Sentiment</option>
          <option value="positive">Positive</option>
          <option value="negative">Negative</option>
          <option value="neutral">Neutral</option>
        </select>
      </div>

      {loading && !articles.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Scanning social feeds...</span>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((item, idx) => {
          const cfg = SENTIMENT_CONFIG[item.sentiment]
          return (
            <div key={item.url || idx} className="card card-hover p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={clsx(cfg.badge, 'text-[10px]')}>{cfg.label}</span>
                    <span className="text-[10px] text-text-muted">{item.source}</span>
                    <span className="text-[10px] text-text-muted">&middot;</span>
                    <span className="text-[10px] text-text-muted">{relativeTime(item.timestamp || item.published)}</span>
                  </div>
                  <h3 className="text-sm font-medium text-text-primary leading-snug">{item.title}</h3>
                  {(item.summary || item.description) && (
                    <p className="text-xs text-text-secondary mt-1 line-clamp-2">{item.summary || item.description}</p>
                  )}
                </div>
                {item.url && (
                  <a href={item.url} target="_blank" rel="noopener" className="text-text-muted hover:text-text-primary flex-shrink-0">
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {!loading && filtered.length === 0 && (
        <div className="card p-8 text-center">
          <p className="text-sm text-text-muted">No social articles found matching your filters.</p>
        </div>
      )}
    </div>
  )
}
